// @file Library security store
import { ALERT_ICON } from '@@/bits/confirmation_dialog'
import { ALERT_SIGN_EMOJI } from '@@/bits/emoji'
import { captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { trustedHtml, type SafeHtml } from '@@/bits/html_parser'
import { n__, __ } from '@@/bits/intl'
import { getSearchParams, reload } from '@@/bits/location'
import { NATIVE_HOST } from '@@/bits/url'
import {
  GoogleAppLicensingSettings as GoogleAppLicensingAPI,
  LibraryOauthSettings as LibraryOauthMethodAPI,
  LibraryPermissions as LibraryPermissionsApi,
  LibraryUserEmailDomain as LibraryUserEmailDomainAPI,
  Saml as SamlApi,
} from '@@/dashboard/padlet_api'
import { ApiErrorCode, HttpCode, SnackbarNotificationType } from '@@/enums'
import { useDashboardSettingsStore } from '@@/pinia/dashboard_settings'
import {
  OzConfirmationDialogBoxButtonScheme,
  useGlobalConfirmationDialogStore,
} from '@@/pinia/global_confirmation_dialog'
import { useGlobalInputDialogStore } from '@@/pinia/global_input_dialog'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSettingsNavigationStore } from '@@/pinia/settings_navigation'
import type {
  GoogleAppLicensingCreateResponse,
  GoogleAppLicensingSettingsResponse,
  Id,
  JsonApiData,
  Library,
  LibraryOauthSettings,
  LibraryUserEmailDomain,
  SamlConnectionApiResponse,
  SchoolLibraryPermission,
  TeamLibraryPermission,
} from '@@/types'
import { LibraryOauthProvider } from '@@/types'
import type { JsonAPIResource } from '@padlet/arvo'
import { FetchError } from '@padlet/fetch'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

enum LibrarySecurityStatus {
  Loading = 'Loading',
  Completed = 'Completed',
  Errored = 'Errored',
}

// Default SAML admin roles for teacher assignment (matches backend AFFILIATION_ADMIN_ROLES)
const DEFAULT_SAML_ADMIN_ROLES_STRING = 'faculty, staff, employee'

enum InputStatus {
  Editing,
  Loading,
  Viewing,
  Errored,
}

const useSettingsLibrarySecurityStore = defineStore('settingsLibrarySecurity', () => {
  const globalSnackbarStore = useGlobalSnackbarStore()
  const settingsNavigationStore = useSettingsNavigationStore()
  const globalInputDialogStore = useGlobalInputDialogStore()
  const globalConfirmationDialogStore = useGlobalConfirmationDialogStore()
  const dashboardSettingsStore = useDashboardSettingsStore()

  // State
  const libraryUserEmailDomains = ref<LibraryUserEmailDomain[]>([])
  const libraryUserEmailDomainStatus = ref(LibrarySecurityStatus.Loading)

  const LibraryOauthSettings = ref<LibraryOauthSettings | null>(null)
  const libraryOauthMethodsStatus = ref(LibrarySecurityStatus.Loading)

  const googleAppLicenseData = ref<GoogleAppLicensingCreateResponse>({ orderId: '', redeemUri: '' })
  const isLibraryUsingGoogleAppLicensing = ref(false)
  const isGoogleAppLicenseLoading = ref(false)

  // Member login OTP (email verification for password login).
  const requireLoginOtp = ref(false)
  const isMemberLoginOtpLoading = ref(false)
  const memberLoginOtpLibraryId = ref<number>()
  // Monotonic token so a slower earlier request cannot overwrite the result of a
  // later one (rapid library switches or on/off toggles). Only the response from
  // the most recently issued request is applied to state.
  let latestMemberLoginOtpRequestId = 0
  let latestMemberLoginOtpInitRequestId = 0

  // Verified account linking opt-out. The backend field is negative
  // (verifiedAccountLinkingDisabled). Null means not fetched or unavailable; loading
  // is tracked separately. The settings row consumes the positive inverse
  // (verificationRequired), inverted only at this store boundary.
  const verifiedAccountLinkingDisabled = ref<boolean | null>(null)
  const isVerifiedAccountLinkingLoading = ref(false)
  const verificationRequired = computed(() => verifiedAccountLinkingDisabled.value !== true)
  const verifiedAccountLinkingLibraryId = ref<number>()
  let latestVerifiedAccountLinkingRequestId = 0
  let latestVerifiedAccountLinkingInitRequestId = 0

  // TODO: add Clever and Classlink SSO ENG-14288,  ENG-14289
  const ALLOWED_OAUTH_METHODS = [
    LibraryOauthProvider.GOOGLE,
    LibraryOauthProvider.MICROSOFT,
    LibraryOauthProvider.APPLE,
    LibraryOauthProvider.PASSWORD,
    LibraryOauthProvider.CLEVER,
    LibraryOauthProvider.CLASSLINK,
  ]

  // Should reflect value of equivalent constant in Libraries::UserDomains::RequestApprovalController
  const MAX_PENDING_DOMAINS_PER_LIBRARY = 10

  // Getters
  const isLoadingLibrarySecurity = computed(() =>
    dashboardSettingsStore.isSchoolLibrary
      ? libraryUserEmailDomainStatus.value === LibrarySecurityStatus.Loading
      : false,
  )
  const libraryId = computed(() => settingsNavigationStore.currentLibraryId)
  const libraryName = computed(() => settingsNavigationStore.currentLibrary?.name)
  const libraryOauthMethods = computed(() =>
    LibraryOauthSettings.value?.oauth.filter((method) => ALLOWED_OAUTH_METHODS.includes(method.name)),
  )
  const canDisableOauthMethods = computed(() => {
    // If SAML login is enabled, the owner can disable all oauth methods
    if (xSamlLoginSection.value) return true
    const enabledMethods = libraryOauthMethods.value?.filter((method) => method.enabled) ?? []
    return enabledMethods.length > 1
  })
  const cleverDistrictId = computed(() => LibraryOauthSettings.value?.cleverDistrictId)
  const classlinkTenantId = computed(() => LibraryOauthSettings.value?.classlinkTenantId)
  const xLibraryUserEmailDomains = computed(() => libraryUserEmailDomains.value.length > 0)
  // Only schools and SAML-whitelisted teams (owner's team_library_saml flip) can set up SAML.
  // The library_login_otp_toggle flip lets non-whitelisted teams reach this page, so gate the
  // SAML section and its fetch on this per-library capability rather than on library type.
  const xSamlSection = computed<boolean>(
    () =>
      isAppUsing('librarySamlLogin') && settingsNavigationStore.currentLibrary?.libraryAccess?.canSetupSaml === true,
  )

  // Actions
  async function initialize(): Promise<void> {
    if (libraryId.value === null) {
      void globalSnackbarStore.setSnackbar({
        message: __('Error initializing state'),
        notificationType: SnackbarNotificationType.error,
      })
      return
    }
    // Only fetch SAML info when this library can actually set up SAML (schools and
    // SAML-whitelisted teams). Teams reaching this page via the login-OTP toggle are not
    // whitelisted, so fetching would 403 and surface "Error fetching SAML login info".
    if (xSamlSection.value) {
      void fetchSamlLoginInfo()
    }
    if (dashboardSettingsStore.isSchoolLibrary) {
      const promises = [fetchLibraryUserEmailDomain(libraryId.value), fetchLibraryGoogleAppLicensing(libraryId.value)]

      if (isAppUsing('schoolLibrarySSO')) {
        promises.push(fetchLibraryOauthSettings(libraryId.value))
      }

      await Promise.all(promises)
    }
  }

  async function fetchLibraryUserEmailDomain(libraryId: number): Promise<void> {
    try {
      libraryUserEmailDomainStatus.value = LibrarySecurityStatus.Loading
      const response = await LibraryUserEmailDomainAPI.fetch({ libraryId })
      const emailDomains = (response.data as Array<JsonAPIResource<LibraryUserEmailDomain>>)?.map(
        (emailDomain) => emailDomain.attributes,
      )
      libraryUserEmailDomains.value = emailDomains
      libraryUserEmailDomainStatus.value = LibrarySecurityStatus.Completed
    } catch (e) {
      captureFetchException(e, { source: 'DashboardSettingsLibraryFetchLibraryUserEmailDomain' })
      libraryUserEmailDomainStatus.value = LibrarySecurityStatus.Errored
      void globalSnackbarStore.genericFetchError()
    }
  }

  async function requestApprovalLibraryUserEmailDomain(
    domains: Array<Partial<LibraryUserEmailDomain>>,
  ): Promise<boolean> {
    try {
      //  Validate library
      if (libraryId.value == null) {
        void globalSnackbarStore.setSnackbar({
          message: __('Error adding approved domains'),
          notificationType: SnackbarNotificationType.error,
        })
        return false
      }
      // Validate domains to add
      const existingPendingDomainsCount = libraryUserEmailDomains.value.filter((domain) => domain.pending).length
      if (domains.length + existingPendingDomainsCount > MAX_PENDING_DOMAINS_PER_LIBRARY) {
        void globalSnackbarStore.setSnackbar({
          message: __('You can have at most %{maxPendingDomainsPerLibrary} domains pending approval at a time', {
            maxPendingDomainsPerLibrary: MAX_PENDING_DOMAINS_PER_LIBRARY,
          }),
          notificationType: SnackbarNotificationType.error,
        })
        return false
      }

      const response = await LibraryUserEmailDomainAPI.requestApproval({ libraryId: libraryId.value, domains })
      const emailDomains = (response.data as Array<JsonAPIResource<LibraryUserEmailDomain>>)?.map(
        (emailDomain) => emailDomain.attributes,
      )
      libraryUserEmailDomains.value = [...libraryUserEmailDomains.value, ...emailDomains]
      globalSnackbarStore.setSnackbar({
        message: n__(
          'Your domain is pending approval. Our member services team will follow up shortly.',
          'Your domains are pending approval. Our member services team will follow up shortly.',
          emailDomains.length,
        ),
        notificationType: SnackbarNotificationType.success,
      })
      return true
    } catch (e) {
      captureFetchException(e, { source: 'DashboardSettingsLibraryRequestApprovalLibraryUserEmailDomain' })
      void globalSnackbarStore.genericFetchError()
      return false
    }
  }

  async function deleteLibraryUserEmailDomain(domainName: string): Promise<void> {
    try {
      if (libraryId.value == null) {
        void globalSnackbarStore.genericFetchError()
        return
      }
      await LibraryUserEmailDomainAPI.delete({ libraryId: libraryId.value, domainName })
      libraryUserEmailDomains.value = libraryUserEmailDomains.value.filter((domain) => domain.domainName !== domainName)
    } catch (e) {
      captureFetchException(e, { source: 'DashboardSettingsLibraryDeleteLibraryUserEmailDomain' })
      void globalSnackbarStore.genericFetchError()
    }
  }

  async function askToDeleteLibraryUserEmailDomain(domainName: string): Promise<void> {
    void globalConfirmationDialogStore.openConfirmationDialog({
      ...ALERT_SIGN_EMOJI,
      title: __('Remove domain?'),
      body: __('Users with this domain will no longer be automatically added to your school.'),
      confirmButtonText: __('Confirm'),
      cancelButtonText: __('Nevermind'),
      buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
      forceFullWidthButtons: true,
      afterConfirmCallbacks: [async () => await deleteLibraryUserEmailDomain(domainName)],
    })
  }

  async function fetchLibraryOauthSettings(libraryId: number): Promise<void> {
    try {
      const response = await LibraryOauthMethodAPI.fetch({ libraryId })
      const oauthSettings = (response.data as JsonAPIResource<LibraryOauthSettings>).attributes
      LibraryOauthSettings.value = oauthSettings
    } catch (e) {
      captureFetchException(e, { source: 'DashboardSettingsLibraryFetchLibraryOuathMethods' })
      libraryOauthMethodsStatus.value = LibrarySecurityStatus.Errored
      void globalSnackbarStore.genericFetchError()
    }
  }

  async function fetchLibraryGoogleAppLicensing(libraryId: number): Promise<void> {
    try {
      const response = await LibraryOauthMethodAPI.fetchGoogleAppLicensing({ libraryId })
      const googleAppLicensingSettings = (response.data as JsonAPIResource<GoogleAppLicensingSettingsResponse>)
        .attributes
      isLibraryUsingGoogleAppLicensing.value = googleAppLicensingSettings.enabled
      googleAppLicenseData.value.orderId = googleAppLicensingSettings.orderId ?? ''
      googleAppLicenseData.value.redeemUri = googleAppLicensingSettings.redeemUri ?? ''
    } catch (e) {
      captureFetchException(e, { source: 'DashboardSettingsLibraryFetchLibraryGoogleAppLicensing' })
      void globalSnackbarStore.genericFetchError()
    }
  }

  function readRequireMemberLoginOtp(
    response: Awaited<ReturnType<typeof LibraryPermissionsApi.fetch>>,
  ): boolean | undefined {
    const attrs = (response.data as JsonAPIResource<SchoolLibraryPermission | TeamLibraryPermission>)?.attributes
    return attrs?.requireMemberLoginOtp
  }

  async function initializeMemberLoginOtp(library: Library): Promise<void> {
    isMemberLoginOtpLoading.value = true
    memberLoginOtpLibraryId.value = library.id
    // Reset to the safe default so a failed fetch or a library without the
    // setting never shows the previously loaded library's value.
    requireLoginOtp.value = false
    const requestId = (latestMemberLoginOtpRequestId += 1)
    const initRequestId = (latestMemberLoginOtpInitRequestId += 1)
    try {
      const response = await LibraryPermissionsApi.fetch(library.id)
      if (requestId !== latestMemberLoginOtpRequestId) return
      requireLoginOtp.value = readRequireMemberLoginOtp(response) ?? false
    } catch (e) {
      if (requestId !== latestMemberLoginOtpRequestId) return
      captureFetchException(e, { source: 'DashboardSettingsLibraryFetchMemberLoginOtp' })
      void globalSnackbarStore.genericFetchError()
    } finally {
      if (initRequestId === latestMemberLoginOtpInitRequestId) {
        isMemberLoginOtpLoading.value = false
      }
    }
  }

  async function setMemberLoginOtpRequired(required: boolean): Promise<void> {
    if (memberLoginOtpLibraryId.value == null) return
    const requestId = (latestMemberLoginOtpRequestId += 1)
    try {
      const response = await LibraryPermissionsApi.updateMemberLoginOtp(memberLoginOtpLibraryId.value, required)
      if (requestId !== latestMemberLoginOtpRequestId) return
      const value = readRequireMemberLoginOtp(response)
      if (value != null) {
        requireLoginOtp.value = value
      }
    } catch (e) {
      if (requestId !== latestMemberLoginOtpRequestId) return
      captureFetchException(e, { source: 'DashboardSettingsLibraryUpdateMemberLoginOtp' })
      void globalSnackbarStore.genericFetchError()
    }
  }

  function readVerifiedAccountLinkingDisabled(
    response: Awaited<ReturnType<typeof LibraryPermissionsApi.fetch>>,
  ): boolean | null | undefined {
    const attrs = (response.data as JsonAPIResource<SchoolLibraryPermission | TeamLibraryPermission>)?.attributes
    return attrs?.verifiedAccountLinkingDisabled
  }

  async function initializeVerifiedAccountLinking(library: Library): Promise<void> {
    isVerifiedAccountLinkingLoading.value = true
    verifiedAccountLinkingLibraryId.value = library.id
    // Reset to null as the safe default so a failed fetch or a library without the
    // setting cannot retain the previously loaded library's value.
    verifiedAccountLinkingDisabled.value = null
    const requestId = (latestVerifiedAccountLinkingRequestId += 1)
    const initRequestId = (latestVerifiedAccountLinkingInitRequestId += 1)
    try {
      const response = await LibraryPermissionsApi.fetch(library.id)
      if (requestId !== latestVerifiedAccountLinkingRequestId) return
      verifiedAccountLinkingDisabled.value = readVerifiedAccountLinkingDisabled(response) ?? null
    } catch (e) {
      if (requestId !== latestVerifiedAccountLinkingRequestId) return
      captureFetchException(e, { source: 'DashboardSettingsLibraryFetchVerifiedAccountLinking' })
      void globalSnackbarStore.genericFetchError()
    } finally {
      if (initRequestId === latestVerifiedAccountLinkingInitRequestId) {
        isVerifiedAccountLinkingLoading.value = false
      }
    }
  }

  async function setVerificationRequired(required: boolean, libraryId: number): Promise<void> {
    // The turn-off confirmation dialog is global and can outlive a library switch:
    // without this check a late confirm would write the opt-out to whichever
    // library is loaded by then, not the one the admin clicked the toggle for.
    if (libraryId !== verifiedAccountLinkingLibraryId.value) return
    const requestId = (latestVerifiedAccountLinkingRequestId += 1)
    const previousDisabled = verifiedAccountLinkingDisabled.value
    verifiedAccountLinkingDisabled.value = !required
    try {
      // The single negative/positive inversion point: the API stores `disabled`.
      const response = await LibraryPermissionsApi.updateVerifiedAccountLinkingDisabled(libraryId, !required)
      if (requestId !== latestVerifiedAccountLinkingRequestId) return
      // Fall back to the optimistic value when the response omits the attribute.
      verifiedAccountLinkingDisabled.value = readVerifiedAccountLinkingDisabled(response) ?? !required
    } catch (e) {
      if (requestId !== latestVerifiedAccountLinkingRequestId) return
      verifiedAccountLinkingDisabled.value = previousDisabled
      captureFetchException(e, { source: 'DashboardSettingsLibraryUpdateVerifiedAccountLinking' })
      void globalSnackbarStore.genericFetchError()
    }
  }

  async function updateLibraryOauthMethod(
    oauthMethod: string,
    enable: boolean,
    thirdPartyIdInput?: string,
  ): Promise<void> {
    if (libraryId.value === null) {
      void globalSnackbarStore.setSnackbar({
        message: __('Error updating library login method'),
        notificationType: SnackbarNotificationType.error,
      })
      return
    }

    try {
      updateOauthMethodSettings(oauthMethod, enable)
      const response = await LibraryOauthMethodAPI.update({
        libraryId: libraryId.value,
        oauthMethod,
        enable,
        thirdPartyIdInput,
      })
      const oauthSettings = (response.data as JsonAPIResource<LibraryOauthSettings>).attributes
      LibraryOauthSettings.value = oauthSettings
    } catch (e) {
      updateOauthMethodSettings(oauthMethod, !enable)
      if (e instanceof FetchError) {
        try {
          const parsedErrorMessage = JSON.parse(e.message)
          const error = parsedErrorMessage.errors[0]

          if (e.status === HttpCode.UnprocessableEntity) {
            if (error.code === ApiErrorCode.LAST_OAUTH_PROVIDER) {
              void globalSnackbarStore.setSnackbar({
                message: __('Cannot disable only login option'),
                notificationType: SnackbarNotificationType.error,
              })
            }
          } else if (e.status === HttpCode.Conflict) {
            if (error.code === ApiErrorCode.CLEVER_DISTRICT_ID_ALREADY_IN_USE) {
              void globalSnackbarStore.setSnackbar({
                message: __('That Clever district id is already in use by another organization'),
                notificationType: SnackbarNotificationType.error,
              })
            } else if (error.code === ApiErrorCode.CLASSLINK_TENANT_ID_ALREADY_IN_USE) {
              void globalSnackbarStore.setSnackbar({
                message: __('That ClassLink tenant id is already in use by another organization'),
                notificationType: SnackbarNotificationType.error,
              })
            }
          } else {
            void globalSnackbarStore.genericFetchError()
          }
        } catch (_jsonException) {
          void globalSnackbarStore.genericFetchError()
        }
        captureFetchException(e, { source: 'DashboardSettingsLibraryUpdateLibraryOauthMethod' })
      }
    }
  }

  function handleGoogleAppLicensingError(errorMessage: string): void {
    isGoogleAppLicenseLoading.value = false
    isLibraryUsingGoogleAppLicensing.value = false
    globalSnackbarStore.setSnackbar({
      message: errorMessage,
      notificationType: SnackbarNotificationType.error,
      timeout: 1500,
    })
  }

  function showConfirmationDialogueGoogleAppLicensing(): void {
    globalConfirmationDialogStore.openConfirmationDialog({
      title: __('Enable Google App Licensing?'),
      body: __(
        'Users in %{libraryName} will be signed out and will not be able to sign back in until you turn Google App Licensing off or assign them a license in the Google admin panel',
        { libraryName: libraryName.value },
      ),
      confirmButtonText: __('Confirm'),
      afterConfirmCallbacks: [async () => await updateAllowGoogleAppLicensing(true)],
    })
  }

  async function updateAllowGoogleAppLicensing(useGoogleAppLicensing: boolean): Promise<void> {
    if (libraryId.value === null) {
      void globalSnackbarStore.setSnackbar({
        message: __('Error updating library login method'),
        notificationType: SnackbarNotificationType.error,
      })
      return
    }

    isLibraryUsingGoogleAppLicensing.value = useGoogleAppLicensing
    isGoogleAppLicenseLoading.value = true

    try {
      if (
        !useGoogleAppLicensing ||
        (googleAppLicenseData.value.orderId !== '' && googleAppLicenseData.value.redeemUri !== '')
      ) {
        /* const updateLibrarySettingsRes = */ await LibraryOauthMethodAPI.updateGoogleAppLicensing({
          libraryId: libraryId.value,
          enable: useGoogleAppLicensing,
          orderId: googleAppLicenseData.value.orderId,
          redeemUri: googleAppLicenseData.value.redeemUri,
        })
      } else {
        const licenseCreationRes = await GoogleAppLicensingAPI.createLicenses(libraryId.value)
        const licenseData = (licenseCreationRes.data as JsonAPIResource<GoogleAppLicensingCreateResponse>).attributes

        /* const updateLibrarySettingsRes = */ await LibraryOauthMethodAPI.updateGoogleAppLicensing({
          libraryId: libraryId.value,
          enable: useGoogleAppLicensing,
          orderId: licenseData.orderId ?? '',
          redeemUri: licenseData.redeemUri ?? '',
        })
        googleAppLicenseData.value = licenseData
      }
      useDashboardSettingsStore().fetchViewableLibraries()
    } catch (e) {
      handleGoogleAppLicensingError(__('Error setting up app licensing. Please try again.'))
      captureFetchException(e, { source: 'LibrarySecuritySettingsUpdateAllowGoogleAppLicensing' })
    } finally {
      isGoogleAppLicenseLoading.value = false
    }
  }

  function showInputDialogThirdPartyTenantId(oauthMethodName: LibraryOauthProvider): void {
    const inputDialogValues = getGlobalInputDialogValues(oauthMethodName)
    globalInputDialogStore.openInputDialog(inputDialogValues)
  }

  // helpers

  function updateOauthMethodSettings(oauthMethodName: string, enable: boolean): void {
    const updatedOauthMethods =
      libraryOauthMethods.value?.map((method) => {
        return method.name === oauthMethodName ? { name: method.name, enabled: enable } : method
      }) ?? []
    LibraryOauthSettings.value = { ...LibraryOauthSettings.value, oauth: updatedOauthMethods }
  }

  function getGlobalInputDialogValues(oauthMethod: LibraryOauthProvider): {
    title: string
    subtitle: SafeHtml
    inputPlaceholder: string
    inputLabel: string
    inputValue: string
    inputMaxLength: number
    inputAriaLabel: string
    submitButtonText: string
    submitActions: Array<(params: { inputValue: string }) => void>
    validationActions: Array<(params: { inputValue: string }) => void>
  } {
    const cleverValues = {
      title: __('Add your Clever district ID'),
      // prettier-ignore
      subtitle: trustedHtml(
        __(
          // goodcheck-disable-line
          'This is available in your admin’s Clever Management Console. Read more here: <a id="link-external" href="%{cleverArticleUrl}" target="_blank" rel="noopener nofollow" class="oz-text-link-light-secondary dark:oz-text-link-dark-secondary break-all">%{cleverArticleUrl}</a>',
          {
            cleverArticleUrl: 'https://support.clever.com/hc/s/articles/000001500?language=en_US',
          },
        ),
        'translated markup; article URL is a static constant',
      ),
      inputPlaceholder: __('E.g. %{tenantIdExample}', { tenantIdExample: 'AF5322XF' }),
      inputLabel: __('Clever district ID'),
      inputValue: cleverDistrictId.value ?? '',
      inputMaxLength: 25,
      inputAriaLabel: __('Enter your Clever district ID'),
      submitButtonText: __('Add'),
      submitActions: [
        ({ inputValue }): void => {
          void updateLibraryOauthMethod(oauthMethod, true, inputValue)
        },
      ],
      validationActions: [
        ({ inputValue }) => {
          const isContainsAlphaNumericOnly: boolean = /^[0-9a-zA-Z]*$/.test(inputValue)
          const validationFunction = isContainsAlphaNumericOnly

          globalInputDialogStore.setInputIsValid(validationFunction)
          globalInputDialogStore.setValidationMessage(validationFunction ? '' : __('Invalid district ID'))
          globalInputDialogStore.setSubmitButtonDisable(!validationFunction || inputValue.length === 0)
        },
      ],
    }

    const classlinkValues = {
      title: __('Add your ClassLink tenant ID'),
      // prettier-ignore
      subtitle: trustedHtml(
        __(
          // goodcheck-disable-line
          'This is available in your admin’s ClassLink Management Console. Read more here: <a id="link-external" href="%{classlinkArticleUrl}" target="_blank" rel="noopener nofollow" class="oz-text-link-light-secondary dark:oz-text-link-dark-secondary break-all">%{classlinkArticleUrl}</a>',
          {
            classlinkArticleUrl: 'https://help.classlink.com/s/article/Management-Console',
          },
        ),
        'translated markup; article URL is a static constant',
      ),
      inputPlaceholder: __('E.g. %{tenantIdExample}', { tenantIdExample: 351800 }),
      inputLabel: __('ClassLink tenant ID'),
      inputValue: classlinkTenantId.value ?? '',
      inputMaxLength: 6,
      inputAriaLabel: __('Enter your ClassLink tenant ID'),
      submitButtonText: __('Add'),
      submitActions: [
        ({ inputValue }): void => {
          void updateLibraryOauthMethod(oauthMethod, true, inputValue)
        },
      ],
      validationActions: [
        ({ inputValue }) => {
          const isContainsZeroOrMoreNumbersOnly: boolean = /^[0-9]*$/.test(inputValue)
          const validationFunction = isContainsZeroOrMoreNumbersOnly

          globalInputDialogStore.setInputIsValid(validationFunction)
          globalInputDialogStore.setValidationMessage(validationFunction ? '' : __('Invalid tenant ID'))
          globalInputDialogStore.setSubmitButtonDisable(!validationFunction || inputValue.length === 0)
        },
      ],
    }

    return oauthMethod === LibraryOauthProvider.CLASSLINK ? classlinkValues : cleverValues
  }

  // SAML login

  const samlConnectionId = ref<Id | null>(null)
  const xSamlLoginSection = ref<boolean>(false)
  const samlSpEntityId = ref<string | null>(null) // It looks like a URL, e.g. https://padlet.dev/auth/saml/sp/2476480

  const hasSamlLoginRegistered = computed<boolean>(() => samlConnectionId.value != null)
  const libraryIdWithEpoch = computed<string>(() => {
    if (hasSamlLoginRegistered.value && samlSpEntityId.value === null) return ''
    // <library_id><epoch time in hours>
    return samlSpEntityId.value != null
      ? samlSpEntityId.value.substring(samlSpEntityId.value.lastIndexOf('/') + 1)
      : `${String(libraryId.value)}${Math.floor(Date.now() / 3600000)}`
  })
  const samlSPMetadataUrl = computed<string>(() => {
    // Before the launch of the SAML setup self-serve tool, most SAML connections did not have a samlSpEntityId,
    // and identity providers used https://padlet.com/auth/saml/metadata on their end. Refer to https://github.com/padlet/mozart/pull/16797.
    // After the launch, identity providers will use the URL format: https://padlet.com/auth/saml/metadata/<library_id><epoch time in hours>
    return `https://${NATIVE_HOST}/auth/saml/metadata/${libraryIdWithEpoch.value}`
  })

  function toggleSamlLoginSection(): void {
    xSamlLoginSection.value = !xSamlLoginSection.value
  }

  const samlIdpMetadataUrl = ref<string>('')
  const samlIdpMetadataUrlInput = ref<string>('')
  const samlIdpMetadataUrlValidationMessage = ref<string>('')
  const samlIdpMetadataUrlInputStatus = ref<InputStatus>(InputStatus.Viewing)
  const isUpdatingSamlIdpMetadataUrl = computed(() => samlIdpMetadataUrlInputStatus.value === InputStatus.Loading)

  function setSamlIdpMetadataUrl(payload: {
    input: string
    inputStatus: InputStatus
    validationMessage?: string
  }): void {
    samlIdpMetadataUrlInput.value = payload.input
    samlIdpMetadataUrlInputStatus.value = payload.inputStatus
    if (payload.validationMessage !== undefined) {
      samlIdpMetadataUrlValidationMessage.value = payload.validationMessage
    }
  }

  function confirmSamlIdpMetadataUrlRefresh(): void {
    // Capture the current URL for refresh
    const capturedUrl = samlIdpMetadataUrl.value

    // Set status to Loading to prevent blur handler from resetting the input
    samlIdpMetadataUrlInputStatus.value = InputStatus.Loading

    globalConfirmationDialogStore.openConfirmationDialog({
      ...ALERT_ICON,
      title: __('Refresh IdP metadata?'),
      body: __(
        'This will fetch a new certificate and update your SAML configuration. Are you sure you want to proceed?',
      ),
      confirmButtonText: __('Refresh'),
      cancelButtonText: __('Cancel'),
      forceFullWidthButtons: true,
      afterConfirmCallbacks: [async () => await updateSamlIdpMetadataUrl(capturedUrl)],
      afterCancelActions: [
        () => {
          samlIdpMetadataUrlInput.value = samlIdpMetadataUrl.value
          samlIdpMetadataUrlInputStatus.value = InputStatus.Viewing
        },
      ],
    })
  }

  function confirmSamlIdpMetadataUrlUpdate(): void {
    const trimmedSamlIdpMetadataUrl = samlIdpMetadataUrlInput.value.trim()

    if (trimmedSamlIdpMetadataUrl === '') {
      samlIdpMetadataUrlInputStatus.value = InputStatus.Errored
      samlIdpMetadataUrlValidationMessage.value = __('IdP metadata URL can not be blank')
      return
    }

    if (trimmedSamlIdpMetadataUrl === samlIdpMetadataUrl.value) {
      samlIdpMetadataUrlInputStatus.value = InputStatus.Viewing
      return
    }

    // Capture the value before the blur handler can reset it
    const capturedUrl = trimmedSamlIdpMetadataUrl

    // Set status to Loading to prevent blur handler from resetting the input
    samlIdpMetadataUrlInputStatus.value = InputStatus.Loading

    globalConfirmationDialogStore.openConfirmationDialog({
      ...ALERT_ICON,
      title: __('Update IdP metadata URL?'),
      body: __(
        'This will fetch a new certificate and update your SAML configuration. Are you sure you want to proceed?',
      ),
      confirmButtonText: __('Update'),
      cancelButtonText: __('Cancel'),
      forceFullWidthButtons: true,
      afterConfirmCallbacks: [async () => await updateSamlIdpMetadataUrl(capturedUrl)],
      afterCancelActions: [
        () => {
          samlIdpMetadataUrlInput.value = samlIdpMetadataUrl.value
          samlIdpMetadataUrlInputStatus.value = InputStatus.Viewing
        },
      ],
    })
  }

  async function updateSamlIdpMetadataUrl(urlToUpdate?: string): Promise<void> {
    const trimmedSamlIdpMetadataUrl = urlToUpdate ?? samlIdpMetadataUrlInput.value.trim()
    const isSameUrl = trimmedSamlIdpMetadataUrl === samlIdpMetadataUrl.value

    try {
      samlIdpMetadataUrlInputStatus.value = InputStatus.Loading
      await SamlApi.updateSamlConnection(
        samlConnectionId.value as Id,
        { idpMetadataUrl: trimmedSamlIdpMetadataUrl },
        libraryId.value as Id,
      )
      samlIdpMetadataUrl.value = trimmedSamlIdpMetadataUrl
      samlIdpMetadataUrlInput.value = trimmedSamlIdpMetadataUrl
      globalSnackbarStore.setSnackbar({
        message: isSameUrl
          ? __('IdP metadata URL refreshed successfully')
          : __('IdP metadata URL updated successfully'),
        notificationType: SnackbarNotificationType.success,
      })
      setTimeout(() => {
        reload()
      }, 2000)
    } catch (e) {
      samlIdpMetadataUrlInput.value = samlIdpMetadataUrl.value
      globalSnackbarStore.setSnackbar({
        message: __('Error changing IdP metadata URL'),
        notificationType: SnackbarNotificationType.error,
      })
      captureFetchException(e, { source: 'LibraryInfoUpdateSamlIdpMetadataUrl' })
    } finally {
      samlIdpMetadataUrlInputStatus.value = InputStatus.Viewing
    }
  }

  const samlEmailAttribute = ref<string>('')
  const samlEmailAttributeInput = ref<string>('')
  const samlEmailAttributeValidationMessage = ref<string>('')
  const samlEmailAttributeInputStatus = ref<InputStatus>(InputStatus.Viewing)

  function setSamlEmailAttribute(payload: {
    input: string
    inputStatus: InputStatus
    validationMessage?: string
  }): void {
    samlEmailAttributeInput.value = payload.input
    samlEmailAttributeInputStatus.value = payload.inputStatus
    if (payload.validationMessage !== undefined) {
      samlEmailAttributeValidationMessage.value = payload.validationMessage
    }
  }

  async function updateSamlEmailAttribute(): Promise<void> {
    const trimmedSamlEmailAttribute = samlEmailAttributeInput.value.trim()

    if (trimmedSamlEmailAttribute === '') {
      samlEmailAttributeInputStatus.value = InputStatus.Errored
      samlEmailAttributeValidationMessage.value = __('Email attribute can not be blank')
      return
    }

    if (trimmedSamlEmailAttribute === samlEmailAttribute.value) {
      samlEmailAttributeInputStatus.value = InputStatus.Viewing
      return
    }

    try {
      await SamlApi.updateSamlConnection(
        samlConnectionId.value as Id,
        {
          emailAttribute: trimmedSamlEmailAttribute,
        },
        libraryId.value as Id,
      )
      samlEmailAttribute.value = trimmedSamlEmailAttribute
      samlEmailAttributeInput.value = trimmedSamlEmailAttribute
    } catch (e) {
      samlEmailAttributeInput.value = samlEmailAttribute.value
      globalSnackbarStore.setSnackbar({
        message: __('Error changing email attribute.'),
        notificationType: SnackbarNotificationType.error,
      })
      captureFetchException(e, { source: 'LibraryInfoUpdateSamlEmailAttribute' })
    } finally {
      samlEmailAttributeInputStatus.value = InputStatus.Viewing
    }
  }

  const samlNameAttribute = ref<string>('')
  const samlNameAttributeInput = ref<string>('')
  const samlNameAttributeValidationMessage = ref<string>('')
  const samlNameAttributeInputStatus = ref<InputStatus>(InputStatus.Viewing)

  function setSamlNameAttribute(payload: {
    input: string
    inputStatus: InputStatus
    validationMessage?: string
  }): void {
    samlNameAttributeInput.value = payload.input
    samlNameAttributeInputStatus.value = payload.inputStatus
    if (payload.validationMessage !== undefined) {
      samlNameAttributeValidationMessage.value = payload.validationMessage
    }
  }

  async function updateSamlNameAttribute(): Promise<void> {
    const trimmedSamlNameAttribute = samlNameAttributeInput.value.trim()

    if (trimmedSamlNameAttribute === '') {
      samlNameAttributeInputStatus.value = InputStatus.Errored
      samlNameAttributeValidationMessage.value = __('Name attribute can not be blank')
      return
    }

    if (trimmedSamlNameAttribute === samlNameAttribute.value) {
      samlNameAttributeInputStatus.value = InputStatus.Viewing
      return
    }

    try {
      await SamlApi.updateSamlConnection(
        samlConnectionId.value as Id,
        { nameAttribute: trimmedSamlNameAttribute },
        libraryId.value as Id,
      )
      samlNameAttribute.value = trimmedSamlNameAttribute
      samlNameAttributeInput.value = trimmedSamlNameAttribute
    } catch (e) {
      samlNameAttributeInput.value = samlNameAttribute.value
      globalSnackbarStore.setSnackbar({
        message: __('Error changing name attribute.'),
        notificationType: SnackbarNotificationType.error,
      })
      captureFetchException(e, { source: 'LibraryInfoUpdateSamlNameAttribute' })
    } finally {
      samlNameAttributeInputStatus.value = InputStatus.Viewing
    }
  }

  const samlRoleAttribute = ref<string>('')
  const samlRoleAttributeInput = ref<string>('')
  const samlRoleAttributeInputStatus = ref<InputStatus>(InputStatus.Viewing)

  function setSamlRoleAttribute(payload: { input: string; inputStatus: InputStatus }): void {
    samlRoleAttributeInput.value = payload.input
    samlRoleAttributeInputStatus.value = payload.inputStatus
  }

  async function updateSamlRoleAttribute(): Promise<void> {
    const trimmedSamlRoleAttribute = samlRoleAttributeInput.value.trim()

    if (trimmedSamlRoleAttribute === samlRoleAttribute.value) {
      samlRoleAttributeInputStatus.value = InputStatus.Viewing
      return
    }

    try {
      const wasEmpty = samlRoleAttribute.value === ''
      const isNowEmpty = trimmedSamlRoleAttribute === ''

      // Backend handles setting/clearing admin_roles based on role_attribute changes
      await SamlApi.updateSamlConnection(
        samlConnectionId.value as Id,
        { roleAttribute: trimmedSamlRoleAttribute },
        libraryId.value as Id,
      )
      samlRoleAttribute.value = trimmedSamlRoleAttribute
      samlRoleAttributeInput.value = trimmedSamlRoleAttribute

      // Update local state to match backend behavior
      if (isNowEmpty) {
        // Backend clears admin_roles when role is cleared
        samlAdminRoles.value = []
        samlAdminRolesInput.value = ''
      } else if (wasEmpty) {
        // Backend sets default admin_roles when role is set for the first time
        const defaultAdminRoles = DEFAULT_SAML_ADMIN_ROLES_STRING.split(',').map((role) => role.trim())
        samlAdminRoles.value = defaultAdminRoles
        samlAdminRolesInput.value = DEFAULT_SAML_ADMIN_ROLES_STRING
      }
    } catch (e) {
      samlRoleAttributeInput.value = samlRoleAttribute.value
      globalSnackbarStore.setSnackbar({
        message: __('Error changing role attribute.'),
        notificationType: SnackbarNotificationType.error,
      })
      captureFetchException(e, { source: 'LibraryInfoUpdateSamlRoleAttribute' })
    } finally {
      samlRoleAttributeInputStatus.value = InputStatus.Viewing
    }
  }

  const samlAdminRoles = ref<string[]>([])
  const samlAdminRolesInput = ref<string>(DEFAULT_SAML_ADMIN_ROLES_STRING)
  const samlAdminRolesInputStatus = ref<InputStatus>(InputStatus.Viewing)

  function setSamlAdminRoles(payload: { input: string; inputStatus: InputStatus }): void {
    samlAdminRolesInput.value = payload.input
    samlAdminRolesInputStatus.value = payload.inputStatus
  }

  async function updateSamlAdminRoles(): Promise<void> {
    const trimmedInput = samlAdminRolesInput.value.trim()
    const newAdminRoles =
      trimmedInput === ''
        ? []
        : trimmedInput
            .split(',')
            .map((role) => role.trim())
            .filter(Boolean)

    // Check if input matches current stored value (compare actual arrays, not display strings)
    const normalizedInput = newAdminRoles.join(', ')
    const normalizedCurrent = samlAdminRoles.value.join(', ')

    if (normalizedInput === normalizedCurrent) {
      samlAdminRolesInputStatus.value = InputStatus.Viewing
      return
    }

    try {
      await SamlApi.updateSamlConnection(
        samlConnectionId.value as Id,
        { adminRoles: newAdminRoles },
        libraryId.value as Id,
      )
      samlAdminRoles.value = newAdminRoles
      samlAdminRolesInput.value = newAdminRoles.join(', ')
    } catch (e) {
      samlAdminRolesInput.value = samlAdminRoles.value.join(', ')
      globalSnackbarStore.setSnackbar({
        message: __('Error changing teacher role values.'),
        notificationType: SnackbarNotificationType.error,
      })
      captureFetchException(e, { source: 'LibraryInfoUpdateSamlAdminRoles' })
    } finally {
      samlAdminRolesInputStatus.value = InputStatus.Viewing
    }
  }

  const samlConnectionName = ref<string>('')
  const samlConnectionNameInput = ref<string>('')
  const samlConnectionNameInputStatus = ref<InputStatus>(InputStatus.Viewing)

  function setSamlConnectionName(payload: { input: string; inputStatus: InputStatus }): void {
    samlConnectionNameInput.value = payload.input
    samlConnectionNameInputStatus.value = payload.inputStatus
  }

  async function updateSamlConnectionName(): Promise<void> {
    const trimmedSamlConnectionName = samlConnectionNameInput.value.trim()

    if (trimmedSamlConnectionName === samlConnectionName.value) {
      samlConnectionNameInputStatus.value = InputStatus.Viewing
      return
    }

    try {
      await SamlApi.updateSamlConnection(
        samlConnectionId.value as Id,
        { connectionName: trimmedSamlConnectionName },
        libraryId.value as Id,
      )
      samlConnectionName.value = trimmedSamlConnectionName
      samlConnectionNameInput.value = trimmedSamlConnectionName
    } catch (e) {
      samlConnectionNameInput.value = samlConnectionName.value
      globalSnackbarStore.setSnackbar({
        message: __('Error changing login text.'),
        notificationType: SnackbarNotificationType.error,
      })
      captureFetchException(e, { source: 'LibraryInfoUpdateSamlConnectionName' })
    } finally {
      samlConnectionNameInputStatus.value = InputStatus.Viewing
    }
  }

  const isSamlDevModeEnabled = ref<boolean>(true)

  function setSamlDevMode(value: boolean): void {
    const title = value ? __('Enable Dev mode?') : __('Disable Dev mode?')
    const body = value
      ? __('Enabling dev mode will reset your login method to email only. Are you sure you want to proceed?')
      : __('Disabling dev mode will launch SAML login to all users. Are you sure you want to proceed?')
    globalConfirmationDialogStore.openConfirmationDialog({
      ...ALERT_ICON,
      title,
      body,
      confirmButtonText: __('Change'),
      cancelButtonText: __('Nevermind'),
      forceFullWidthButtons: true,
      afterConfirmCallbacks: [async () => await updateSamlDevMode(value)],
    })
  }

  async function updateSamlDevMode(isEnabled: boolean): Promise<void> {
    try {
      isSamlDevModeEnabled.value = isEnabled
      await SamlApi.updateSamlConnection(samlConnectionId.value as Id, { devMode: isEnabled }, libraryId.value as Id)
    } catch (e) {
      isSamlDevModeEnabled.value = !isEnabled

      globalSnackbarStore.setSnackbar({
        message: __('Error updating dev mode.'),
        notificationType: SnackbarNotificationType.error,
      })
      captureFetchException(e, { source: 'LibraryInfoUpdateSamlDevMode' })
    }
  }

  function resetSamlState(): void {
    samlConnectionId.value = null
    xSamlLoginSection.value = false
    samlSpEntityId.value = null
    samlIdpMetadataUrl.value = ''
    samlIdpMetadataUrlInput.value = ''
    samlIdpMetadataUrlValidationMessage.value = ''
    samlIdpMetadataUrlInputStatus.value = InputStatus.Viewing
    samlEmailAttribute.value = ''
    samlEmailAttributeInput.value = ''
    samlEmailAttributeValidationMessage.value = ''
    samlEmailAttributeInputStatus.value = InputStatus.Viewing
    samlNameAttribute.value = ''
    samlNameAttributeInput.value = ''
    samlNameAttributeValidationMessage.value = ''
    samlNameAttributeInputStatus.value = InputStatus.Viewing
    samlRoleAttribute.value = ''
    samlRoleAttributeInput.value = ''
    samlRoleAttributeInputStatus.value = InputStatus.Viewing
    samlAdminRoles.value = []
    samlAdminRolesInput.value = DEFAULT_SAML_ADMIN_ROLES_STRING
    samlAdminRolesInputStatus.value = InputStatus.Viewing
    samlConnectionName.value = ''
    samlConnectionNameInput.value = ''
    samlConnectionNameInputStatus.value = InputStatus.Viewing
    isSamlDevModeEnabled.value = true
    isAwaitingCreateSamlConnectionResponse.value = false
  }

  async function fetchSamlLoginInfo(): Promise<void> {
    resetSamlState()

    const targetLibraryId = libraryId.value

    try {
      const response = await SamlApi.fetchSamlConnections(libraryId.value as Id)

      if (libraryId.value !== targetLibraryId) return

      const samlConnections = response.data as Array<JsonApiData<SamlConnectionApiResponse>>

      if (samlConnections.length !== 0) {
        const searchParams = getSearchParams()
        const requestedSamlConnectionId = searchParams.saml_connection_id
        const matchedConnection =
          requestedSamlConnectionId != null
            ? samlConnections.find((c) => String(c.attributes.id) === requestedSamlConnectionId)
            : undefined
        const samlConnection = (matchedConnection ?? samlConnections[0]).attributes

        samlConnectionId.value = samlConnection.id
        samlIdpMetadataUrl.value = samlConnection.idpMetadataUrl
        samlIdpMetadataUrlInput.value = samlConnection.idpMetadataUrl
        samlEmailAttribute.value = samlConnection.emailAttribute
        samlEmailAttributeInput.value = samlConnection.emailAttribute
        samlNameAttribute.value = samlConnection.nameAttribute
        samlNameAttributeInput.value = samlConnection.nameAttribute
        samlRoleAttribute.value = samlConnection.roleAttribute
        samlRoleAttributeInput.value = samlConnection.roleAttribute
        samlAdminRoles.value = samlConnection.adminRoles ?? []
        samlAdminRolesInput.value = (samlConnection.adminRoles ?? []).join(', ')
        samlConnectionName.value = samlConnection.connectionName
        samlConnectionNameInput.value = samlConnection.connectionName
        isSamlDevModeEnabled.value = samlConnection.devMode
        samlSpEntityId.value = samlConnection.spEntityId
        xSamlLoginSection.value = true
      } else {
        samlConnectionName.value = 'SAML'
        samlConnectionNameInput.value = 'SAML'
        samlIdpMetadataUrlValidationMessage.value = __('IdP metadata URL can not be blank')
        samlIdpMetadataUrlInputStatus.value = InputStatus.Errored
        samlEmailAttributeValidationMessage.value = __('Email attribute can not be blank')
        samlEmailAttributeInputStatus.value = InputStatus.Errored
        samlNameAttributeValidationMessage.value = __('Name attribute can not be blank')
        samlNameAttributeInputStatus.value = InputStatus.Errored
      }
    } catch (e) {
      globalSnackbarStore.setSnackbar({
        message: __('Error fetching SAML login info'),
        notificationType: SnackbarNotificationType.error,
      })
      captureFetchException(e, { source: 'LibraryInfoFetchSamlLoginInfo' })
    }
  }

  const isAwaitingCreateSamlConnectionResponse = ref<boolean>(false)

  async function createSamlConnection(): Promise<void> {
    const trimmedSamlIdpMetadataUrl = samlIdpMetadataUrlInput.value.trim()

    if (trimmedSamlIdpMetadataUrl === '') {
      samlIdpMetadataUrlInputStatus.value = InputStatus.Errored
      samlIdpMetadataUrlValidationMessage.value = __('IdP metadata URL can not be blank')
      return
    }

    if (trimmedSamlIdpMetadataUrl === samlIdpMetadataUrl.value) {
      samlIdpMetadataUrlInputStatus.value = InputStatus.Viewing
      return
    }

    const trimmedSamlEmailAttribute = samlEmailAttributeInput.value.trim()

    if (trimmedSamlEmailAttribute === '') {
      samlEmailAttributeInputStatus.value = InputStatus.Errored
      samlEmailAttributeValidationMessage.value = __('Email attribute can not be blank')
      return
    }

    if (trimmedSamlEmailAttribute === samlEmailAttribute.value) {
      samlEmailAttributeInputStatus.value = InputStatus.Viewing
      return
    }

    const trimmedSamlNameAttribute = samlNameAttributeInput.value.trim()

    if (trimmedSamlNameAttribute === '') {
      samlNameAttributeInputStatus.value = InputStatus.Errored
      samlNameAttributeValidationMessage.value = __('Name attribute can not be blank')
      return
    }

    if (trimmedSamlNameAttribute === samlNameAttribute.value) {
      samlNameAttributeInputStatus.value = InputStatus.Viewing
      return
    }

    const trimmedRoleAttribute = samlRoleAttributeInput.value.trim()
    const trimmedAdminRolesInput = samlAdminRolesInput.value.trim()
    const adminRoles =
      trimmedAdminRolesInput === ''
        ? []
        : trimmedAdminRolesInput
            .split(',')
            .map((role) => role.trim())
            .filter(Boolean)

    try {
      isAwaitingCreateSamlConnectionResponse.value = true
      const response = await SamlApi.createSamlConnection(
        {
          idpMetadataUrl: trimmedSamlIdpMetadataUrl,
          emailAttribute: trimmedSamlEmailAttribute,
          nameAttribute: trimmedSamlNameAttribute,
          roleAttribute: trimmedRoleAttribute,
          adminRoles,
          connectionName: samlConnectionNameInput.value.trim() !== '' ? samlConnectionNameInput.value.trim() : 'SAML',
          devMode: isSamlDevModeEnabled.value,
          prespecifiedSpEntityId: `https://${NATIVE_HOST}/auth/saml/sp/${libraryIdWithEpoch.value}`,
        },
        libraryId.value as Id,
      )
      const samlConnection = (response.data as Array<JsonApiData<SamlConnectionApiResponse>>)[0].attributes

      samlConnectionId.value = samlConnection.id
      samlIdpMetadataUrl.value = samlConnection.idpMetadataUrl
      samlIdpMetadataUrlInput.value = samlConnection.idpMetadataUrl
      samlEmailAttribute.value = samlConnection.emailAttribute
      samlEmailAttributeInput.value = samlConnection.emailAttribute
      samlNameAttribute.value = samlConnection.nameAttribute
      samlNameAttributeInput.value = samlConnection.nameAttribute
      samlRoleAttribute.value = samlConnection.roleAttribute
      samlRoleAttributeInput.value = samlConnection.roleAttribute
      samlAdminRoles.value = samlConnection.adminRoles ?? []
      samlAdminRolesInput.value = (samlConnection.adminRoles ?? []).join(', ')
      samlConnectionName.value = samlConnection.connectionName
      samlConnectionNameInput.value = samlConnection.connectionName
      isSamlDevModeEnabled.value = samlConnection.devMode

      globalSnackbarStore.setSnackbar({
        message: __('SAML connection created.'),
        notificationType: SnackbarNotificationType.success,
      })
    } catch (e) {
      globalSnackbarStore.setSnackbar({
        message: __('Error creating SAML connection'),
        notificationType: SnackbarNotificationType.error,
      })
      captureFetchException(e, { source: 'LibraryInfoCreateSamlConnection' })
    } finally {
      isAwaitingCreateSamlConnectionResponse.value = false
    }
  }

  return {
    // State
    libraryUserEmailDomains,
    libraryUserEmailDomainStatus,
    isLibraryUsingGoogleAppLicensing,
    isGoogleAppLicenseLoading,
    googleAppLicenseData,

    // Getters
    isLoadingLibrarySecurity,
    libraryOauthMethods,
    canDisableOauthMethods,
    cleverDistrictId,
    classlinkTenantId,
    xLibraryUserEmailDomains,
    hasSamlLoginRegistered,
    samlSPMetadataUrl,
    xSamlSection,
    xSamlLoginSection,
    isAwaitingCreateSamlConnectionResponse,

    samlIdpMetadataUrl,
    samlIdpMetadataUrlInput,
    samlIdpMetadataUrlValidationMessage,
    samlIdpMetadataUrlInputStatus,

    samlEmailAttribute,
    samlEmailAttributeInput,
    samlEmailAttributeValidationMessage,
    samlEmailAttributeInputStatus,

    samlNameAttribute,
    samlNameAttributeInput,
    samlNameAttributeValidationMessage,
    samlNameAttributeInputStatus,

    samlRoleAttribute,
    samlRoleAttributeInput,
    samlRoleAttributeInputStatus,

    samlAdminRoles,
    samlAdminRolesInput,
    samlAdminRolesInputStatus,

    samlConnectionName,
    samlConnectionNameInput,
    samlConnectionNameInputStatus,

    isSamlDevModeEnabled,
    isUpdatingSamlIdpMetadataUrl,

    // Actions
    initialize,
    updateLibraryOauthMethod,
    updateAllowGoogleAppLicensing,
    showInputDialogThirdPartyTenantId,
    showConfirmationDialogueGoogleAppLicensing,
    createSamlConnection,
    toggleSamlLoginSection,
    setSamlIdpMetadataUrl,
    confirmSamlIdpMetadataUrlUpdate,
    confirmSamlIdpMetadataUrlRefresh,
    setSamlEmailAttribute,
    updateSamlEmailAttribute,
    setSamlNameAttribute,
    updateSamlNameAttribute,
    setSamlRoleAttribute,
    updateSamlRoleAttribute,
    setSamlAdminRoles,
    updateSamlAdminRoles,
    setSamlConnectionName,
    updateSamlConnectionName,
    setSamlDevMode,
    updateSamlDevMode,
    requestApprovalLibraryUserEmailDomain,
    askToDeleteLibraryUserEmailDomain,

    // Member login OTP
    requireLoginOtp,
    isMemberLoginOtpLoading,
    initializeMemberLoginOtp,
    setMemberLoginOtpRequired,

    // Verified account linking
    verificationRequired,
    verifiedAccountLinkingDisabled,
    isVerifiedAccountLinkingLoading,
    initializeVerifiedAccountLinking,
    setVerificationRequired,
  }
})
export { DEFAULT_SAML_ADMIN_ROLES_STRING, InputStatus, useSettingsLibrarySecurityStore }
